import { CloseButton, ComparisonButton, SaveButton } from '@ui/shared/Button';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  closeInnerPopup,
  closePopup,
  closeTopLevelPopup,
  openInnerPopup,
  openPopup,
  openTopLevelPopup,
} from '../../../data/providers/popupsProvider';
import {
  comparisonPopulationSettingsProvider,
  IComparisonPopulationSetting,
} from '../../../data/providers/settings/comparisonPopulationSettingsProvider';
import { IExclusiveDateOption } from '../../../data/providers/settings/dates/dateRangeProvider';
import {
  EZScorePreference,
  IRange,
  ISelectOption,
  Level,
} from '../../../data/types';
import { saveComparisonPopulationSetting } from '../../../data/writers/settings/comparisonPopulationSetting';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter, CardFooterRight } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { FormContainer } from '../../shared/form/FormContainer';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';
import { ComparisonSettings } from './ComparisonSettings';

interface IPopupProps {
  onClose: () => void;
  level: Level;
  onSave: () => void;
}
const Popup = (props: IPopupProps): React.ReactElement => {
  const [settings, setSettings] =
    React.useState<IComparisonPopulationSetting>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [initialSettings, setInitialSettings] =
    React.useState<IComparisonPopulationSetting>();

  /**
   * @description subscribe to the current settings
   */
  React.useEffect((): (() => void) => {
    setLoading(true);
    const subscriber = comparisonPopulationSettingsProvider().subscribe(
      (mSettings: IComparisonPopulationSetting): void => {
        setSettings(mSettings);
        setInitialSettings(mSettings);
        setLoading(false);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  const save = (): void => {
    setSaving(true);
    /// only signal loading if something actually changed
    if (!isEqual(initialSettings, settings)) {
      props.onSave();
    }
    saveComparisonPopulationSetting(settings).then((): void => {
      setSaving(false);
      props.onClose();
    });
  };

  /**
   * @description handles change to the entity
   * @param entity
   */
  const handleEntityChange = (entity: ISelectOption): void => {
    setSettings({
      ...settings,
      entity,
    });
  };

  /**
   * @description handles date change
   */

  const handleDateChange = (
    dateRangeType: IExclusiveDateOption,
    dateRangeValues: IRange
  ): void => {
    setSettings({
      ...settings,
      dateRangeType,
      dateRangeValues,
    });
  };

  /**
   * @description handle change to zscore preference
   * @param pref
   */
  const handleZScorePreferenceChange = (pref: EZScorePreference): void => {
    setSettings({
      ...settings,
      zScorePreference: pref,
    });
  };

  const Container = props.level === 1 ? InnerPopupCard : Card;

  return (
    <Container size='medium' onClose={props.onClose} noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Comparison Settings</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {saving && <LinearLoading />}
          {loading ? (
            <LinearLoading />
          ) : (
            <FormContainer>
              <ComparisonSettings
                handleEntityChange={handleEntityChange}
                handleDateChange={handleDateChange}
                handleZScorePreferenceChange={handleZScorePreferenceChange}
                settings={settings}
              />
            </FormContainer>
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardHeaderLeft />
          <CardFooterRight>
            <SaveButton onClick={save} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </Container>
  );
};

interface IProps extends RouteComponentProps {
  level?: Level;
  style?: React.CSSProperties;
  setLoading: (v: boolean) => void;
}

export const ComparisonSettingsPopup = withRouter(
  (props: IProps): React.ReactElement => {
    const open = (): void => {
      const onSave = (): void => {
        props.setLoading(true);
      };
      if (props.level === 1) {
        openInnerPopup(
          <Popup
            onClose={closeInnerPopup(props.history)}
            level={props.level}
            onSave={onSave}
          />,
          props.history,
          'comparison-settings'
        );
      } else if (props.level === 0) {
        openPopup(
          <Popup
            onClose={closePopup(props.history)}
            level={props.level}
            onSave={onSave}
          />,
          props.history,
          'comparison-settings'
        );
      } else {
        openTopLevelPopup(
          <Popup
            onClose={closeTopLevelPopup}
            level={props.level}
            onSave={onSave}
          />
        );
      }
    };

    return (
      <Wrapper style={props.style}>
        <ComparisonButton onClick={open} />
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  align-self: flex-end;
  padding-top: 5px;
`;
